/**
 * App state: one Design signal, derived validation/closed-form/schematic, and
 * effects that debounce worker solves and RLGC sweeps off design changes.
 */
import { computed, effect, signal } from "@preact/signals";
import { tandEffClosedForm } from "@kazam/xsec";
import { Design, toCoaxSpec } from "../model/fill";
import { FILLS, closedForm, schematic, validateDesign } from "../model/registry";
import { condFromPreset } from "../model/materials";
import { solver } from "../solver/client";
import type { SolveResultMsg, SweepResultMsg } from "../solver/protocol";
import { designFromHash, hashFromDesign } from "./persistence";

export type ViewTab = "xsec" | "field" | "rlgc";

function defaultDesign(): Design {
  return {
    fill: FILLS.homogeneous.defaults(),
    unit: "mm",
    conductor: condFromPreset("copper"),
    sweep: { f0: 1e6, f1: 2e10, n: 201, log: true },
    accuracy: "normal",
  };
}

export const design = signal<Design>(designFromHash() ?? defaultDesign());
export const view = signal<ViewTab>("xsec");
export const fieldRes = signal<128 | 256 | 512>(256);
export const fieldQuantity = signal<"V" | "E">("V");
export const showMesh = signal(false);
export const engineReady = signal(false);

export const issues = computed(() => validateDesign(design.value));
export const hasErrors = computed(() => issues.value.some((i) => i.severity === "error"));
export const cf = computed(() => closedForm(design.value));
export const schem = computed(() => schematic(design.value));

export interface SolveState {
  status: "idle" | "solving" | "done" | "error";
  result?: SolveResultMsg;
  error?: string;
  /** True while a newer solve is running but an older result is still shown. */
  stale?: boolean;
}

export const solveState = signal<SolveState>({ status: "idle" });

export interface SweepState {
  status: "idle" | "running" | "done" | "error";
  result?: SweepResultMsg;
  error?: string;
}

export const sweepState = signal<SweepState>({ status: "idle" });

export function updateDesign(mutate: (d: Design) => Design): void {
  design.value = mutate(design.value);
}

solver.ready().then(() => (engineReady.value = true));

let solveTimer: ReturnType<typeof setTimeout> | undefined;
let sweepTimer: ReturnType<typeof setTimeout> | undefined;

/** Debounced solve on any design / view-option change. */
effect(() => {
  const d = design.value;
  const wantField = view.value === "field";
  const res = fieldRes.value;
  const wantE = fieldQuantity.value === "E";
  const mesh = showMesh.value;
  if (hasErrors.value) {
    clearTimeout(solveTimer);
    return;
  }
  const spec = toCoaxSpec(d);
  clearTimeout(solveTimer);
  solveTimer = setTimeout(() => {
    solveState.value = { ...solveState.value, status: "solving", stale: !!solveState.value.result };
    solver
      .solve(spec, {
        accuracy: d.accuracy,
        outlines: true,
        ...(wantField ? { field: { nx: res, ny: res, wantE } } : {}),
        ...(mesh && wantField ? { mesh: true } : {}),
      })
      .then((result) => {
        solveState.value = { status: "done", result };
      })
      .catch((e: Error) => {
        if (e.message === "superseded" || e.message === "solver restarted") return;
        solveState.value = { status: "error", error: e.message };
      });
  }, 250);
});

/** Debounced RLGC sweep when the loss view is active. */
effect(() => {
  const d = design.value;
  if (view.value !== "rlgc" || hasErrors.value) return;
  const spec = toCoaxSpec(d);
  const cfv = cf.value;
  // Effective homogeneous dielectric: closed form when available, else the
  // last numeric eps_eff (off-locus combined) with the energy-weighted tand.
  const er = cfv.available ? cfv.epsEff! : solveState.value.result?.epsEff;
  if (!er) return;
  const tand = tandEffClosedForm(spec);
  const rq = d.conductor.roughnessUm * 1e-6;
  const cond = {
    sigma: d.conductor.sigma,
    muR: d.conductor.muR,
    roughness: rq,
    roughnessModel: d.conductor.roughnessModel,
  };
  clearTimeout(sweepTimer);
  sweepTimer = setTimeout(() => {
    sweepState.value = { ...sweepState.value, status: "running" };
    solver
      .sweep(spec, { ...d.sweep, inner: cond, outer: cond, er, tand })
      .then((result) => (sweepState.value = { status: "done", result }))
      .catch((e: Error) => {
        if (e.message === "superseded" || e.message === "solver restarted") return;
        sweepState.value = { status: "error", error: e.message };
      });
  }, 250);
});

/** Persist the design to the URL hash (shareable links). */
effect(() => {
  const h = hashFromDesign(design.value);
  if (typeof history !== "undefined") history.replaceState(null, "", h);
});
